import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Grid, Loader, Message } from 'semantic-ui-react';
import { withTranslation } from 'react-i18next';

import { namespaces, TRANSLATION_FILES } from '../../../constants/translation';
import Timeline from '../../../components/UpdatedComponents/UCTimeline';
import BankCases from '../../../components/Opportunities/BankCases';
import { fetchM } from '../../../actions/fetchM';

const ACTIVITY_LIMIT = 10;

// Temporarily hidden by product decision. The code is kept in place — flip to
// `true` to re-enable the loan activity timeline.
const SHOW_LOAN_TIMELINE = false;

const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '');
};

const getActivityTitle = (type) => {
  switch (type) {
    case 'new': return 'Lead Created';
    case 'update': return 'Updated';
    case 'update_pending': return 'Moved to Pending';
    case 'nudge': return 'Nudge';
    case 'closed': return 'Closed';
    case 'closed_reject': return 'Closed — Rejected';
    case 'closed_success': return 'Closed — Success';
    case 'message': return 'Remark';
    case 'reassign': return 'Banker Reassigned';
    default: return type || 'Activity';
  }
};

class Loan extends Component {
  static contextTypes = {
    store: PropTypes.object,
  }

  static propTypes = {
    id: PropTypes.string,
    t: PropTypes.func,
  }

  constructor(props) {
    super(props);
    this.state = {
      loanLead: null,
      bankCases: [],
      loadingLead: false,
      leadNotFound: false,
      activityLogs: [],
      loadingLogs: false,
      hasMore: false,
      offset: 0,
    };
  }

  componentDidMount() {
    this.mounted = true;
    const { id } = this.props;
    if (id) {
      this.fetchLoanLead(id);
      if (SHOW_LOAN_TIMELINE) this.fetchActivityLogs(id, 0);
    }
  }

  componentDidUpdate(prevProps) {
    const { id } = this.props;
    if (id && id !== prevProps.id) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        loanLead: null,
        bankCases: [],
        activityLogs: [],
        offset: 0,
        hasMore: false,
        leadNotFound: false,
      });
      this.fetchLoanLead(id);
      if (SHOW_LOAN_TIMELINE) this.fetchActivityLogs(id, 0, true);
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  fetchLoanLead = async (id) => {
    const { store } = this.context;
    if (!id || !store) return;

    if (this.mounted) this.setState({ loadingLead: true, leadNotFound: false });

    try {
      const res = await store.dispatch(fetchM(`/opportunity/${id}/loan_tracker/`, {
        credentials: 'include',
        method: 'GET',
        ignoreNotFound: true,
        headers: { 'Content-Type': 'application/json' },
      }));

      if (!this.mounted) return;

      if (res.ok) {
        const lead = await res.json();
        // H-152: Strip sensitive company fields — only keep what the UI needs
        const safeLead = {
          ...lead,
          company: lead.company
            ? { currencyCode: lead.company.currencyCode }
            : undefined,
        };
        this.setState({
          loanLead: safeLead,
          bankCases: lead.bankCases || [],
        });
      } else if (res.status === 404) {
        this.setState({ loanLead: null, bankCases: [], leadNotFound: true });
      }
    } catch (err) {
      // silently handled
    } finally {
      if (this.mounted) this.setState({ loadingLead: false });
    }
  }

  fetchActivityLogs = async (id, offset, reset = false) => {
    const { store } = this.context;
    if (!id || !store) return;

    if (this.mounted) this.setState({ loadingLogs: true });

    try {
      const res = await store.dispatch(fetchM(
        `/opportunity/${id}/loan_tracker/activity_logs/?limit=${ACTIVITY_LIMIT}&offset=${offset}`,
        {
          credentials: 'include',
          method: 'GET',
          ignoreNotFound: true,
          headers: { 'Content-Type': 'application/json' },
        },
      ));

      if (!this.mounted) return;
      const activityLogToken = res.headers.get('X-Csrf-Token');
      if (activityLogToken) {
        this.setState({ activityLogToken });
      }

      if (res.ok) {
        const items = await res.json();
        const total = parseInt(res.headers.get('M-Data-count'), 10) || 0;
        const nextOffset = offset + (items ? items.length : 0);
        this.setState(prevState => ({
          activityLogs: (reset || offset === 0)
            ? (items || [])
            : [...prevState.activityLogs, ...(items || [])],
          offset: nextOffset,
          hasMore: nextOffset < total,
        }));
      }
      // 404 means no lead exists yet — show empty timeline
    } catch (err) {
      // silently handled
    } finally {
      if (this.mounted) this.setState({ loadingLogs: false });
    }
  }

  handleLoadMore = () => {
    const { id } = this.props;
    const { offset, loadingLogs } = this.state;
    if (loadingLogs || !id) return;
    this.fetchActivityLogs(id, offset);
  }

  render() {
    const { t, id } = this.props;
    const {
      bankCases, loanLead, activityLogs, loadingLead, loadingLogs, hasMore, leadNotFound, activityLogToken,
    } = this.state;

    const projectCurrencies = (loanLead && loanLead.company && loanLead.company.currencyCode) || '';

    const timelineData = activityLogs.map(item => ({
      id: item.id,
      title: t(getActivityTitle(item.type), { ns: TRANSLATION_FILES.translation }),
      description: stripHtml(item.message),
      date: item.createdAt,
    }));

    return (
      <Grid>
        <Grid.Row>
          <Grid.Column width={SHOW_LOAN_TIMELINE ? 12 : 16}>
            <h3>{t('Loan applied', { ns: TRANSLATION_FILES.translation })}</h3>
            {loadingLead && <Loader active inline="centered" />}
            {!loadingLead && leadNotFound && (
              <Message info content={t('Loan has not been applied for this booking', { ns: TRANSLATION_FILES.translation })} />
            )}
            {!loadingLead && !leadNotFound && (
              <BankCases
                bankCases={bankCases}
                projectCurrencies={projectCurrencies}
                leadLoanAmount={loanLead ? Number(loanLead.loanAmount) : undefined}
                opportunityID={id}
              />
            )}
          </Grid.Column>
          {SHOW_LOAN_TIMELINE && (
          <Grid.Column width={4}>
            {!loadingLead && (
            <Timeline
              data={timelineData}
              hasMore={hasMore}
              onLoadMore={this.handleLoadMore}
              loading={loadingLogs}
              title={t('Loan Timeline', { ns: TRANSLATION_FILES.translation })}
              loadMoreLabel={t('Load more', { ns: TRANSLATION_FILES.screens })}
              bankCases={bankCases}
              opportunityID={id}
              token={activityLogToken}
              fetchActivityLogs={() => this.fetchActivityLogs(id, 0, true)}
              hideAddRemark={leadNotFound}
            />
            )}
          </Grid.Column>
          )}
        </Grid.Row>
      </Grid>
    );
  }
}

export default withTranslation(namespaces)(Loan);



// WEBPACK FOOTER //
// ./src/containers/Opportunities/Overview/Loan.jsx